import { useEffect, useState } from 'react';
import api from '../../services/api';
import { setCategoryDisplayCache } from './categoryCache';
import {
  EVENT_CATEGORIES as FALLBACK_CODES,
  CATEGORY_LABELS as FALLBACK_LABELS,
} from '../weeklyView/constants';
import { DEFAULT_CATEGORY_HEX, TOKEN_TO_HEX } from './colorUtils';

const FALLBACK_CATEGORIES = FALLBACK_CODES.map((code, index) => ({
  id: `fallback-${code}`,
  code,
  label: FALLBACK_LABELS[code] || code,
  color: TOKEN_TO_HEX[
    ({ HO: 'blue', HNO: 'primary', Astreinte: 'amber', Congés: 'green', Formation: 'yellow', Absence: 'rose' })[code]
  ] || DEFAULT_CATEGORY_HEX,
  counts_as_unavailable: !['HO', 'HNO'].includes(code),
  sort_order: index + 1,
  is_active: true,
}));

/**
 * Load planning event categories from the API (active only for non-admins).
 * Falls back to hardcoded constants while loading / on error.
 */
export default function useEventCategories() {
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.getCategories({ is_active: true });
        const list = Array.isArray(response) ? response : [];
        if (!cancelled && list.length > 0) {
          setCategoryDisplayCache(list);
          setCategories(list);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const codes = categories.map((c) => c.code);
  const labels = Object.fromEntries(categories.map((c) => [c.code, c.label]));
  const colors = Object.fromEntries(categories.map((c) => [c.code, c.color]));
  const defaultCode = codes.includes('HO') ? 'HO' : codes[0] || 'HO';

  return {
    categories,
    codes,
    labels,
    colors,
    defaultCode,
    loading,
    error,
    colorForCategory: (code) => colors[code] || DEFAULT_CATEGORY_HEX,
    labelForCategory: (code) => labels[code] || FALLBACK_LABELS[code] || code,
  };
}
